/* eslint-disable max-lines */
import wretch, { WretcherError, WretcherResponse } from 'wretch';
import debounce from '@mui/material/utils/debounce';
import { useBuildVars } from '../config';
import { useApiHeaders, ExtraHeaders } from './useApiHeaders';
import { useRef } from 'react';

export type ObjectMap = {
  [key: string]: any;
};

export type ExtraQueryOptions<TO = ObjectMap> = {
  host?: string;
  isLogoutApi?: boolean;
  parseResponse?: (res: WretcherResponse) => TO | Promise<TO>;
  requireResellerId?: boolean;
};

const apiWretcher = wretch()
  .options({
    // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
    // credentials: 'include',
    credentials: 'same-origin', // Default
    // https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
    mode: 'cors',
  })
  .errorType('json');

export class ApiError extends Error {
  status: number;
  response?: any;

  constructor(message: string, status: number, response?: object) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}
function handleError(error: WretcherError): never {
  throw new ApiError(
    error.json?.errors?.[0]?.message ??
      error.json?.error?.message ??
      error.json?.fault?.faultstring ??
      error.json?.error ??
      error.json?.errorMessage ??
      error.json?.title ??
      error.message,
    error.status,
    error.json
  );
}
export function useApiGet<TO = ObjectMap>(options?: ExtraQueryOptions<TO>) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${options?.host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders, options?.requireResellerId).then(
      (headers) =>
        apiCall
          .headers(headers)
          .get()
          .res<TO>((res) => {
            if (options?.parseResponse) {
              return options.parseResponse(res) as TO;
            }
            return res.json() as TO;
          })
          .catch(handleError)
    );
  };
}

export function useApiDownload<TO = ObjectMap>(
  options?: ExtraQueryOptions<TO>
) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${options?.host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders, options?.requireResellerId).then(
      (headers) => apiCall.headers(headers).get().res().catch(handleError)
    );
  };
}

export function useApiDownloadPost<TO = ObjectMap>(
  options?: ExtraQueryOptions<TO>
) {
  const buildVars = useBuildVars();
  const postApiHeaders = useApiHeaders();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders,
    body?: any
  ) {
    const url = `${options?.host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return postApiHeaders(extraHeaders, options?.requireResellerId).then(
      (headers) =>
        apiCall
          .headers(headers)
          .post(body ?? {})
          .res()
          .catch(handleError)
    );
  };
}

export function useApiBlobDownload<TO = ObjectMap>(
  options?: ExtraQueryOptions<TO>
) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${options?.host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders, options?.requireResellerId).then(
      (headers) => apiCall.headers(headers).get().blob().catch(handleError)
    );
  };
}

export function useApiPost<TO = ObjectMap>(options?: ExtraQueryOptions) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders,
    useForDownload?: boolean
  ) {
    const url = `${options?.host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders, options?.requireResellerId).then(
      (headers) => {
        if (useForDownload) {
          return apiCall
            .headers(headers)
            .body(JSON.stringify(body))
            .post()
            .res<TO>((res) => res as TO)
            .catch(handleError);
        } else {
          return apiCall
            .headers(headers)
            .body(JSON.stringify(body))
            .post()
            .json<TO>()
            .catch(handleError);
        }
      }
    );
  };
}

export function useApiFileUpload<TO = ObjectMap>(
  onProgress: (progress: number) => void,
  options?: ExtraQueryOptions
) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();
  const _onProgress = debounce(onProgress, 100);

  const baseUrl = `${options?.host ?? buildVars.API_BASE}`;

  const xhrRef = useRef<XMLHttpRequest>();
  const upload = async function (
    route: string,
    body: FormData,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${baseUrl}${route}`;
    const headers = await getApiHeaders(
      {
        ...extraHeaders,
        'Content-Type': undefined,
      },
      options?.requireResellerId
    );
    return new Promise<TO>((resolve, reject) => {
      xhrRef.current = new XMLHttpRequest();
      const xhr = xhrRef.current;
      xhr.responseType = 'json';
      xhr.upload.onprogress = (e) => _onProgress((e.loaded / e.total) * 100);
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        if (xhr.status === 200) resolve(xhr.response as TO);
        else {
          const errorMessage = () => {
            if (typeof xhr.response === 'string' && xhr.response.length)
              return xhr.response;
            if (xhr.response?.errorMessage) return xhr.response?.errorMessage;
            if (xhr.statusText?.length) return xhr.statusText;
            return 'File upload failed';
          };
          reject(new ApiError(errorMessage(), xhr.status));
        }
      };
      xhr.onerror = (e) => {
        return reject(new Error('File upload failed', { cause: e }));
      };
      xhr.onabort = (e) => {
        return reject(new Error('File upload aborted', { cause: e }));
      };
      xhr.open('POST', url, true);
      Object.entries(headers).forEach(([k, v]) => {
        if (v !== undefined) xhr.setRequestHeader(k, v);
      });
      xhr.send(body);
    });
  };
  return { upload, xhr: xhrRef.current };
}
export function useApiPatch<TO = ObjectMap>(host?: string) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return async function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    const headers = await getApiHeaders(extraHeaders);
    try {
      return await apiCall
        .headers(headers)
        .body(JSON.stringify(body))
        .patch()
        .json<TO>();
    } catch (error: any) {
      return handleError(error);
    }
  };
}
export function useApiDelete<TO = ObjectMap>(host?: string) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    body?: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (body) {
      apiCall = apiCall.body(JSON.stringify(body));
    }
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders).then((headers) =>
      apiCall.headers(headers).delete().json<TO>().catch(handleError)
    );
  };
}
export function useApiPut<TO = ObjectMap>(host?: string) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${host ?? buildVars.API_BASE}${route}`;

    let apiCall = apiWretcher.url(url);
    if (body) {
      apiCall = apiCall.body(JSON.stringify(body));
    }
    if (params) {
      apiCall = apiCall.query(params);
    }
    return getApiHeaders(extraHeaders).then((headers) =>
      apiCall
        .headers(headers)
        .body(JSON.stringify(body))
        .put()
        .json<TO>()
        .catch(handleError)
    );
  };
}
export function useApiBlobDownloadNew<TO = ObjectMap>(
  options?: ExtraQueryOptions<TO>
) {
  const buildVars = useBuildVars();
  const getApiHeaders = useApiHeaders();

  return async function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const [apiRoute] = route.split('](');
    const url = `${options?.host ?? buildVars.API_BASE}${apiRoute}`;
    let request = apiWretcher.url(url);

    if (params) {
      request = request.query(params);
    }

    const headers = await getApiHeaders(
      extraHeaders,
      options?.requireResellerId
    );
    const response = await request
      .headers(headers)
      .get()
      .res((res) => res);
    const blob = await response.blob();
    return blob;
  };
}
